import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PaymentHistorySummary, PaymentHistoryTableData } from 'src/app/models/payment-history.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { SortByOptions } from 'src/app/models/customer.model';
import { OrganizationTeamApiService } from 'src/app/services/organization-team-api.service';
import { InvoiceDataService } from 'src/app/services/invoice-data.service';
import { PermissionsService } from 'shared-lib';

@Component({
  selector: 'app-payment-history-list',
  templateUrl: './payment-history-list.component.html',
  styleUrls: ['./payment-history-list.component.scss']
})
export class PaymentHistoryListComponent implements OnInit {
  paymentHistory: PaymentHistoryTableData[] = [];
  summary: PaymentHistorySummary = {
    totalPayment: 0,
    escrowBalance: 0,
    totalTransactions: 0,
    successful: 0,
    pending: 0,
    failed: 0,
  };
  count: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;
  searchText: string = '';
  currency: string = '';

  searchByOptions = [
    { name: 1, value: 'Application No.', translation: 'CUSTOMERS.SEARCH_BY.APPLICATION_NO' },
    { name: 2, value: 'Account No.', translation: 'CUSTOMERS.SEARCH_BY.ACCOUNT_NO' },
  ];
  selectedSearchType: number = this.searchByOptions[0].name;
  selectedSearchTypeDisplay: string = this.searchByOptions[0].translation;
  showSelectSearchType: boolean = false;

  dateFrom: string = '';
  dateTo: string = '';

  mapSortBy: Record<number, any> = {
    [SortByOptions.NEWEST]: { label: 'Newest', arrow: 'no', translation: 'COMMON.SORT_BY.NEWEST' },
    [SortByOptions.OLDEST]: { label: 'Oldest', arrow: 'no', translation: 'COMMON.SORT_BY.OLDEST' },
  };
  sortByValue: SortByOptions = SortByOptions.NEWEST;
  sortByOptions = SortByOptions;
  sortByLabelValue: any = this.mapSortBy[SortByOptions.NEWEST];
  showSortBy: boolean = false;

  paymentStatusItems = [
    { id: 1, name: 'Success', checked: false, translation: 'COMMON.PAYMENT_STATUS.SUCCESS' },
    { id: 2, name: 'Pending', checked: false, translation: 'COMMON.PAYMENT_STATUS.PENDING' },
    { id: 3, name: 'Failed',  checked: false, translation: 'COMMON.PAYMENT_STATUS.FAILED' },
  ];
  selectedStatuses: number[] = [];

  organizations: { id: number; name: string; checked: boolean }[] = [];
  selectedOrganizations: number[] = [];

  clicked: boolean = false;
  selectedFilters: ChecklistItem[] = [];

  constructor(
    private toastr: ToastrService,
    private invoiceDataService: InvoiceDataService,
    private organizationTeamService: OrganizationTeamApiService,
    public permissionsService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.currency = localStorage.getItem('currency') || '';
    this.setFilters();
    this.getOrganizations();
    this.reloadTable(null, true);
  }

  pageChange(page: number): void {
    this.currentPage = page;
    this.reloadTable();
  }

  // refreshSummary is true only when the filtered dataset changes (filters/search),
  // not for pagination or sorting which don't alter the set the cards aggregate over.
  reloadTable(page: any = null, refreshSummary: boolean = false): void {
    if (page) this.currentPage = page;
    const obj = {
      pageInfo: { page: this.currentPage, pageSize: this.pageSize },
      filterParams: this.getFilterObject()
    };
    this.getAll(obj);
    if (refreshSummary) this.loadSummary(obj);
  }

  sortBy(option: SortByOptions): void {
    this.sortByValue = option;
    this.sortByLabelValue = this.mapSortBy[option];
    this.reloadTable();
  }

  selectStatus(): void {
    this.createFilterCards();
  }

  selectVendor(): void {
    this.createFilterCards();
  }

  applyFilter(): void {
    this.reloadTable(1, true);
  }

  selectSearchType(searchType: any): void {
    const wasSearchActive = !!this.searchText;
    this.selectedSearchType = searchType.name;
    this.selectedSearchTypeDisplay = searchType.translation;
    this.showSelectSearchType = false;
    if (wasSearchActive) {
      this.createFilterCards();
      this.reloadTable(1, true);
    }
  }

  resetFilter(): void {
    this.selectedFilters = [];
    this.searchText = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.selectedStatuses = [];
    this.selectedOrganizations = [];

    this.paymentStatusItems = this.paymentStatusItems.map(s => ({ ...s, checked: false }));
    this.paymentStatusItems = [...this.paymentStatusItems];

    this.organizations = this.organizations.map(o => ({ ...o, checked: false }));
    this.organizations = [...this.organizations];

    this.reloadTable(1, true);
  }

  removeFilter(item: ChecklistItem): void {
    if (item.property == 'Payment Status') {
      this.paymentStatusItems.forEach(s => {
        if (s.name === item.selectedValue) s.checked = false;
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Map Vendor') {
      this.organizations.forEach(o => {
        if (o.name === item.selectedValue) o.checked = false;
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Date From') {
      this.dateFrom = '';
    } else if (item.property == 'Date To') {
      this.dateTo = '';
    } else if (item.property == 'Search') {
      this.searchText = '';
    }

    this.createFilterCards();
    if (this.selectedFilters.length === 0) this.reloadTable(1, true);
  }

  createFilterCards(): void {
    this.selectedFilters = [];

    const selectedStatuses = this.paymentStatusItems.filter(x => x.checked);
    this.selectedStatuses = selectedStatuses.map(x => x.id);

    const selectedOrgs = this.organizations.filter(x => x.checked);
    this.selectedOrganizations = selectedOrgs.map(x => x.id);

    for (const s of selectedStatuses) {
      this.selectedFilters.push(new ChecklistItem('Payment Status', s.name, 'COMMON.PAYMENT_STATUS.SINGLE', s.translation));
    }

    for (const o of selectedOrgs) {
      this.selectedFilters.push(new ChecklistItem('Map Vendor', o.name, 'SURVEYS.MAP_VENDOR'));
    }

    if (this.dateFrom) {
      this.selectedFilters.push(new ChecklistItem('Date From', this.dateFrom, 'COMMON.DATE_FROM'));
    }

    if (this.dateTo) {
      this.selectedFilters.push(new ChecklistItem('Date To', this.dateTo, 'COMMON.DATE_TO'));
    }

    if (this.searchText) {
      this.selectedFilters.push(new ChecklistItem('Search', this.searchText, 'COMMON.SEARCH'));
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Success': return 'approved-building';
      case 'Pending': return 'submitted-building';
      case 'Failed': return 'rejected-building';
      default: return 'assigned-building';
    }
  }

  getStatusKey(status: string): string {
    switch (status) {
      case 'Success': return 'COMMON.PAYMENT_STATUS.SUCCESS';
      case 'Pending': return 'COMMON.PAYMENT_STATUS.PENDING';
      case 'Failed': return 'COMMON.PAYMENT_STATUS.FAILED';
      default: return 'COMMON.PAYMENT_STATUS.UNKNOWN';
    }
  }

  private getFilterObject(): any {
    return {
      search: this.searchText,
      searchType: this.selectedSearchType,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      paymentStatuses: this.selectedStatuses.length > 0 ? this.selectedStatuses : null,
      organizationIds: this.selectedOrganizations.length > 0 ? this.selectedOrganizations : null,
    };
  }

  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject()
    };

    // Remove 'T23:59:59' from the dateTo to avoid errors
    if (obj.filterParams.dateTo) {
      obj.filterParams.dateTo = obj.filterParams.dateTo.replace(
        'T23:59:59',
        ''
      );
    }

    localStorage.setItem(
      'filterPaymentHistory',
      JSON.stringify({ type: 'filterPaymentHistory', filter: obj })
    );
  }

  setFilters() {
    var filter = localStorage.getItem('filterPaymentHistory');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'filterPaymentHistory') return;

    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;

    this.searchText = filterObject.filter.filterParams.search;
    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;
   
    this.selectedStatuses = filterObject.filter.filterParams.paymentStatuses
      ? filterObject.filter.filterParams.paymentStatuses
      : [];
    this.selectedOrganizations = filterObject.filter.filterParams.organizationIds
      ? filterObject.filter.filterParams.organizationIds
      : [];  

    this.paymentStatusItems.forEach((status) => {
      if(this.selectedStatuses.includes(status.id))status.checked = true;  
    });

    this.organizations.forEach((org) => {
      if(this.selectedOrganizations.includes(org.id))org.checked = true;  
    });

    this.createFilterCards();
    localStorage.removeItem('filterPaymentHistory');
  }

  private getAll(obj: any): void {
    this.paymentHistory = [];
    this.count = 0;
    this.invoiceDataService.getAll(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.paymentHistory = data.data.data;
          this.count = data.data.count;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  private loadSummary(obj: any): void {
    this.invoiceDataService.getSummary(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.summary = data.data;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  private getOrganizations(): void {
    this.organizationTeamService.getAllForSelect().subscribe({
      next: (response) => {
        this.organizations = response.data.map((item: any) => ({
          id: item.id,
          name: item.name || '',
          checked: false,
        }));
      },
      error: (_) => this.toastr.error('Error occurred while fetching organizations'),
    });
  }

  download(){
    let obj = this.getFilterObject();
    this.invoiceDataService.download(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
   
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success('File downloaded');
          window.location.href = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }
}
